/**
 * Shrinking Header Component
 */

import Lenis from '@studio-freight/lenis'

import { type App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Shrinking Header');

    const listeners: Array<[Element | Window | Document | Lenis, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            const elements: NodeListOf<HTMLElement> = document.querySelectorAll(`.header-fit`);
            if (elements.length > 0) {
                const lenis = new Lenis();

                // eslint-disable-next-line no-inner-declarations
                function raf(time: DOMHighResTimeStamp) {
                    lenis.raf(time);
                    requestAnimationFrame(raf);
                }

                requestAnimationFrame(raf);

                fitHeaders(listeners, lenis);
            }
        })
        .catch((error: Error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Shrinking Header', error);
        });

    return function destroyShrinkingHeader() {
        unMountShrinkingHeader(listeners);

        app.logger.debug('[DXA] Shrinking Header Component destroyed');
    };
};

function calculateFontSize(text: string, maxWidth: number, context: CanvasRenderingContext2D, fontFamily: string): number {
    let fontSize = 5;  // Start with a small font size
    context.font = `${fontSize}px ${fontFamily}`;  // Set initial font size

    while (context.measureText(text).width < maxWidth) {
        fontSize++;  // Decrease the font size
        context.font = `${fontSize}px ${fontFamily}`;  // Update the font size
    }

    return fontSize;
}

export function fitHeaders(
    listeners: Array<[Element | Window | Document | Lenis, string, EventListenerOrEventListenerObject]>,
    lenis: Lenis
) {
    const elements: NodeListOf<HTMLElement> = document.querySelectorAll(`.header-fit`);
    for (const element of elements) {
        const child = element.querySelector('h1');
        if (child) {
            const containerWidth = child.clientWidth;

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (context) {
                const fontFamily = 'Work Sans';  // Replace with your custom font family
                const fontSize = calculateFontSize(child.textContent ?? "", containerWidth, context, fontFamily);
                child.style.fontSize = `${fontSize - 1}px`;
            }

            child.style.opacity = '1';
            let boundingBox = element.getBoundingClientRect();
            boundingBox = new DOMRect(boundingBox.left, boundingBox.top + document.documentElement.scrollTop, boundingBox.width, boundingBox.height);

            // eslint-disable-next-line @typescript-eslint/no-loop-func
            const setScale = () => {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                const topNavigationBar = document.querySelector('.top-navigation-bar')! as HTMLElement;
                const containerTop = 0;
                const elementHeight = boundingBox.top + element.clientHeight + (topNavigationBar ? topNavigationBar.offsetHeight : 50);
                element.style.height = child.style.fontSize;

                // Slightly increase the scrollTop so that the animation finishes slightly earlier, allowing space for the text to stick around afterwards

                let smallestSize = 0.35;
                const scrollTop = document.documentElement.scrollTop * 1.2;
                if (document.body.scrollWidth < 600) {
                    smallestSize = 0.5;
                }

                if (scrollTop >= containerTop && scrollTop < containerTop + elementHeight) {
                    const ratio = Math.max(1-((scrollTop - containerTop) / elementHeight), smallestSize);
                    child.style.transform = `scale(${ratio})`;
                } else if(scrollTop < containerTop) {
                    child.style.transform = `scale(1)`;
                } else {
                    child.style.transform = `scale(${smallestSize})`;
                }
            }

            lenis.on('scroll', setScale);
            // window.addEventListener('scroll', setScale);
            // listeners.push([window, "scroll", setScale]);
            listeners.push([lenis, "scroll", setScale]);
        }
    }

    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
        timeout = setTimeout(() => {
            clearTimeout(timeout)
            unMountShrinkingHeader(listeners);
            listeners = [];
            fitHeaders(listeners, lenis);
        }, 250)
    };

    window.addEventListener('resize', handleResize);

    listeners.push([window, "resize", handleResize]);
}

export function unMountShrinkingHeader(
    listeners: Array<[Element | Window | Document | Lenis, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        if ((element as Lenis).off) {
            (element as Lenis).off(event, listener);
        } else {
            (element as Element | Window | Document).removeEventListener(event, listener);
        }
    }
}
